import { ArrowBack } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Comments } from '../../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../../models/Comment'
import { PriorityActivityCard } from '../PriorityActivityCard'
import { PriorityStatusButton } from '../PriorityStatusButton'
import { RCNProvidedProfessionalDevelopment } from './RCNProvidedProfessionalDevelopment'
import { STARTEventAttendancePlan } from './STARTEventAttendancePlan'
import { EPLITrainerDevelopmentPlan } from './EPLITrainerDevelopmentPlan'
import { OtherProfessionalDevelopmentResources } from './OtherProfessionalDevelopmentResources'
import {
  type PlanOrganizationTask,
  PlanOrganizationTaskStatus
} from '../../../../../models/PlanOrganizationTask'
import { useParams, useSearchParams } from 'react-router-dom'
import { type Priority } from '../../../../../models/Priority'
import { PriorityOneActivityName } from '../../../../../models/PriorityOneActivityName'
import { useAnnualPlanUserRole } from '../../../../../hooks/use-annualPlanUserRole'
import { useUpdatePlanOrgTaskStatus } from '../../../../../hooks/use-updatePlanOrgTaskStatus'
import { OverviewStatusModals } from '../priorityFour/OverviewStatusModals'
import { AnnualPlanRole } from '../../../../../models/AnnualPlanRole'
import { toast } from 'react-toastify'
import { LockedBanner } from '../../LockedBanner'
import { useAnnualPlanningNavigation } from '../../../../../hooks/use-annualPlanningNavigation'
import { EPLIManagementTool } from './EPLIManagementTool'

interface Props {
  priority: Priority
  planOrganizationTask: PlanOrganizationTask
  onBack: () => void
  onPlanOrgTaskUpdate: (updatedTask: PlanOrganizationTask) => void
  implementationPhase: boolean
}

export const PriorityOneOverview = (props: Props): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { rcnId, planId } = useParams()
  const { userRole, isLoading, error } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)

  const [selectedPriorityActivity, setSelectedPriorityActivity] = useState<string | null>(null)

  const openCommentsQuery = searchParams.get('comments')
  const activityQuery = searchParams.get('activity')

  const {
    isUpdating,
    handleConfirmLockModal,
    updatePlanOrgTaskStatus,
    statusForLockModal,
    setStatusForLockModal,
    statusForSomeItemsLockModal,
    setStatusForSomeItemsLockModal,
    statusForSubmittedToInProgressModal,
    setStatusForSubmittedToInProgressModal,
    statusForSubmittedToAcceptedModal,
    setStatusForSubmittedToAcceptedModal,
    statusForSubmittedToNeedsRevisionModal,
    setStatusForSubmittedToNeedsRevisionModal,
    handleConfirmSubmittedToNeedsRevision,
    disableStatus,
    readOnly,
    trackingReadonly
  } = useUpdatePlanOrgTaskStatus({
    userRole,
    annualPlanId: Number(planId),
    planOrganizationTask: props.planOrganizationTask,
    onPlanOrgTaskUpdate: props.onPlanOrgTaskUpdate,
    priorityActivities: props.priority.activities,
    onBack: props.onBack,
    contractPhase: !props.implementationPhase
  })
  const { handleNavigateToContract } = useAnnualPlanningNavigation()

  const priorityStatusButtonRef = useRef<HTMLButtonElement>(null)
  const [priorityStatusButtonHighlighted, setPriorityStatusButtonHighlighted] = useState(false)

  useEffect(() => {
    if (activityQuery != null) {
      handlePriorityActivityClick(activityQuery)
    }
  }, [activityQuery])

  const handleStatusUpdate = (newStatus: PlanOrganizationTaskStatus): void => {
    updatePlanOrgTaskStatus(newStatus)
    setPriorityStatusButtonHighlighted(false)
  }

  const handlePriorityActivityClick = (priorityActivity: string): void => {
    if (props.planOrganizationTask.status === PlanOrganizationTaskStatus.NotStarted && (userRole === AnnualPlanRole.PlanContributor || userRole === AnnualPlanRole.PlanAdministrator)) {
      toast.error('Set the status to “In Progress” in order to continue.')
      setPriorityStatusButtonHighlighted(true)
      if (priorityStatusButtonRef.current != null) {
        priorityStatusButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    setSelectedPriorityActivity(priorityActivity)
  }

  const handleBackToPriorities = (): void => {
    props.onBack()
  }

  const handleBackToOverview = (): void => {
    // remove activity from search params
    if (searchParams.has('activity')) {
      searchParams.delete('activity')
      setSearchParams(searchParams)
    }
    setSelectedPriorityActivity(null)
    props.onPlanOrgTaskUpdate(props.planOrganizationTask) // trigger reload of the task
  }

  const DisplaySelectedActivity = (_props: { priorityActivityName: string }): JSX.Element => {
    const isTracking = [
      PlanOrganizationTaskStatus.TrackingInProgress,
      PlanOrganizationTaskStatus.TrackingCompleted
    ].includes(props.planOrganizationTask.status)

    switch (_props.priorityActivityName) {
      case PriorityOneActivityName.RcnProvidedProfessionalDevelopment:
        return <RCNProvidedProfessionalDevelopment
          planOrganizationId={props.planOrganizationTask.planOrganizationId}
          planOrganizationTaskStatus={props.planOrganizationTask.status}
          priorityId={props.priority.id}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          implementationPhase={props.implementationPhase}
          onBack={handleBackToOverview}
          handleViewContract={handleNavigateToContract}
          isTracking={isTracking}
        />
      case PriorityOneActivityName.StartEventAttendancePlan:
        return <STARTEventAttendancePlan
          priorityId={props.priority.id}
          annualPLanId={planId != null ? Number(planId) : 0}
          implementationPhase={props.implementationPhase}
          rcnId={rcnId != null ? Number(rcnId) : 0}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          onBack={handleBackToOverview}
          handleViewContract={handleNavigateToContract}
        />
      case PriorityOneActivityName.EpliPlan:
        return <EPLITrainerDevelopmentPlan
          priorityId={props.priority.id}
          annualPLanId={planId != null ? Number(planId) : 0}
          orgId={rcnId != null ? Number(rcnId) : 0}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          implementationPhase={props.implementationPhase}
          onBack={handleBackToOverview}
          isTracking={isTracking}
          planOrgId={props.planOrganizationTask.planOrganizationId}
          handleViewContract={handleNavigateToContract}
        />
      case PriorityOneActivityName.OtherProfessionalDevelopmentResources:
        return <OtherProfessionalDevelopmentResources
          priorityId={props.priority.id}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          onBack={handleBackToOverview}
          implementationPhase={props.implementationPhase}
          handleViewContract={handleNavigateToContract}
        />
      default: return <Typography variant='body1'>Unknown Priority</Typography>
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (error != null) {
    return <Typography variant='body1'>{error}</Typography>
  }

  return (<>
    {readOnly && (
      <LockedBanner status={props.planOrganizationTask.status} />
    )}

    <Card sx={{
      backgroundColor: 'var(--blue-50)',
      borderRadius: '12px',
      padding: '20px 40px 40px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
      position: 'relative',
      overflow: 'visible'
    }}
    >
      {selectedPriorityActivity != null
        ? <DisplaySelectedActivity priorityActivityName={selectedPriorityActivity} />
        : <>
          <Box data-testid='priorityOneOverview' sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                data-testid='priorityActivitiesButton'
                onClick={handleBackToPriorities}
                sx={{
                  padding: '5px 10px',
                  backgroundColor: 'var(--grey-50)',
                  color: 'var(--grey-500)',
                  textTransform: 'none'
                }}
              >
                <ArrowBack sx={{ mr: '10px' }} />
                Priority Activities
              </Button>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '13px'
              }}
              >
                <PriorityStatusButton
                  ref={priorityStatusButtonRef}
                  currentStatus={props.planOrganizationTask.status}
                  onChange={handleStatusUpdate}
                  userRole={userRole}
                  disabled={isUpdating || disableStatus}
                  highlighted={priorityStatusButtonHighlighted}
                  implementationPhase={props.implementationPhase}
                />

                <Comments
                  parentType={CommentParentType.PlanOrganizationTask}
                  parentId={props.priority.planOrganizationTaskId}
                  showComments={openCommentsQuery === 'true'}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Box sx={{
                backgroundColor: 'var(--blue-500)',
                minWidth: '91px',
                minHeight: '91px',
                display: 'grid',
                placeContent: 'center',
                borderRadius: '12px'
              }}
              >
                <Typography variant='h1' sx={{ fontSize: '40px', color: 'var(--white)', fontVariantNumeric: 'lining-nums' }}>
                  01
                </Typography>
              </Box>

              <Typography variant='h1'>
                Professional Learning
              </Typography>
            </Box>
          </Box>

          <Box sx={{ backgroundColor: 'var(--white)', border: '1px solid var(--blue-300)', borderRadius: '8px', padding: '20px' }}>
            <Typography variant='h5' color='var(--blue-700)'>Overall Goals</Typography>
            <Typography variant='body1' sx={{ m: '10px 0' }}>Develop and implement a professional learning (PL) plan for each ISD based on district needs.</Typography>
            <ul style={{ margin: '0' }}>
              <li>Deliver PL using START content, resources, and tools based on identified needs in the PL plan.</li>
              <li>Access START training based on identified needs in the PL plan.</li>
              <li>Provide coaching follow-up as part of the PL plan to ensure implementation of training content.</li>
              <li>Develop an EPLI trainer plan based on relevant data and needs of the ISD.</li>
              <li>Identify at least one training delivered by EPLI Trainers to post on the START events page.</li>
              <li>Disseminate resources and tools (e.g., CETA, START-isms, behavior script template, family engagement practices) that support training content through meetings, newsletters, emails, social media, and other relevant outlets.</li>
            </ul>
          </Box>

          <Typography variant='h2'>Core Activities</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '26px' }}>
            {props.priority.activities.map(priorityActivity =>
              <PriorityActivityCard
                key={priorityActivity.name}
                name={priorityActivity.name}
                started={priorityActivity.started}
                onClick={() => { handlePriorityActivityClick(priorityActivity.name) }}
              />
            )}
          </Box>

          {props.implementationPhase && <EPLIManagementTool priority={props.priority} />}
        </>
      }

      <OverviewStatusModals
        statusForLockModal={statusForLockModal}
        handleConfirmLockModal={handleConfirmLockModal}
        handleCloseLockModal={() => { setStatusForLockModal(null) }}
        statusForSomeItemsLockModal={statusForSomeItemsLockModal}
        handleConfirmNoItemsLockModal={handleConfirmLockModal}
        handleCloseNoItemsLockModal={() => { setStatusForSomeItemsLockModal(null) }}
        statusForSubmittedToInProgressModal={statusForSubmittedToInProgressModal}
        handleConfirmSubmittedToInProgressModal={handleConfirmLockModal}
        handleCloseSubmittedToInProgressModal={() => { setStatusForSubmittedToInProgressModal(null) }}
        statusForSubmittedToAcceptedModal={statusForSubmittedToAcceptedModal}
        handleConfirmSubmittedToAcceptedModal={handleConfirmLockModal}
        handleCloseSubmittedToAcceptedModal={() => { setStatusForSubmittedToAcceptedModal(null) }}
        statusForSubmittedToNeedsRevisionModal={statusForSubmittedToNeedsRevisionModal}
        handleConfirmSubmittedToNeedsRevision={handleConfirmSubmittedToNeedsRevision}
        handleCloseSubmittedToNeedsRevision={() => {
          setStatusForSubmittedToNeedsRevisionModal(null)
        }}
        activities={props.priority.activities}
        planOrganizationTaskId={props.planOrganizationTask.id}
        priorityNumber={props.priority.priorityNumber}
      />
    </Card>
  </>)
}
